import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { bubbleTeaAPI } from '../api/bubbletea';

/**
 * BubbleTeaQRDisplay
 *
 * A full-screen shop-display page showing the QR code that directs
 * customers to the bubble tea ordering page.
 *
 * Route: /bubble-tea/shop-display
 *
 * Usage:
 *  - Navigate to this page on a tablet / display screen in the shop
 *  - Customers scan the QR code to open the ordering page on their phone
 *  - Right-click → Save image / Print to get a printable version
 */

const Page = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #fff8f0;
  padding: 2rem;
  font-family: 'Segoe UI', sans-serif;
`;

const Title = styled.h1`
  font-size: 2rem;
  color: #4a2c2a;
  margin-bottom: 0.5rem;
  text-align: center;
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  color: #7a5c5a;
  margin-bottom: 2rem;
  text-align: center;
`;

const QRImageWrapper = styled.div`
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.12);
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
`;

const QRImage = styled.img`
  width: 280px;
  height: 280px;
  display: block;
`;

const DownloadLink = styled.a`
  margin-top: 1rem;
  font-size: 0.9rem;
  color: #c0392b;
  text-decoration: underline;
  cursor: pointer;
`;

const Footer = styled.p`
  margin-top: 2rem;
  font-size: 0.8rem;
  color: #aaa;
  text-align: center;
`;

const BubbleTeaQRDisplay: React.FC = () => {
  const { t } = useTranslation();
  const qrUrl = bubbleTeaAPI.getMenuQrImageUrl();

  return (
    <Page>
      <Title>{t('bubbleTeaQrDisplay.title')}</Title>
      <Subtitle>{t('bubbleTeaQrDisplay.subtitle')}</Subtitle>

      <QRImageWrapper>
        <QRImage src={qrUrl} alt={t('bubbleTeaQrDisplay.qrImageAlt')} />
        <DownloadLink href={qrUrl} download="bubble-tea-order-qr.png">
          {t('bubbleTeaQrDisplay.downloadLink')}
        </DownloadLink>
      </QRImageWrapper>

      <Footer>{t('bubbleTeaQrDisplay.footer')}</Footer>
    </Page>
  );
};

export default BubbleTeaQRDisplay;
